// jmix-exit (login split): pure, React-free helpers for the TWO login routes. The product keeps two
// distinct unauthenticated entry points instead of one toggled page:
//   • /aerie — admin / service-account interactive login (username + password → POST /api/auth/login)
//   • /login — foster-family passwordless login (magic code → POST /api/auth/magic/{request,verify})
// The brand is the foster portal, so /login is the public default; /aerie is the explicit admin entry.
//
// The SPA has no router (state-based nav in App.tsx). These helpers encode the "which login + where to
// redirect" decision as pure functions so the Gate can canonicalise the address bar and pick the form,
// and so the rules are unit-testable in isolation (App.routing.test.ts) — mirroring setPasswordRoute.ts.

import { NAV, type Role } from "../shell/nav";

export const ADMIN_LOGIN_PATH = "/aerie";
export const FOSTER_LOGIN_PATH = "/login";

/** Which login form an unauthenticated visitor sees: the password form, or the magic-code form. */
export type LoginMode = "admin" | "foster";

// The deep-link route segments that belong to a PASSWORD persona (admin + the deferred self-service
// service-account operator — both authenticate via /api/auth/login, never the magic code). Derived
// from the menu model so it can't drift from the nav: a logged-out hit on one of these lands on the
// admin login. Every other route — the foster portal pages, bare "/", and any unknown path — defaults
// to the foster magic-code login (the public portal brand).
const ADMIN_SEGMENTS: ReadonlySet<string> = new Set(
  NAV.filter((g) => g.roles.some((r) => r === "admin" || r === "self-service"))
    .flatMap((g) => g.items)
    .map((i) => i.route),
);

function normalize(pathname: string | null | undefined): string {
  return (pathname ?? "").replace(/\/+$/, "");
}

/** True when `pathname` is one of the two canonical login routes (trailing slash tolerated). */
export function isLoginPath(pathname: string | null | undefined): boolean {
  const path = normalize(pathname);
  return path === ADMIN_LOGIN_PATH || path === FOSTER_LOGIN_PATH;
}

/**
 * The login form an unauthenticated visit to `pathname` should render:
 *   • /aerie or any admin / self-service deep link → "admin"  (password)
 *   • /login or any foster deep link, bare "/", unknown path → "foster" (magic code)
 */
export function loginModeForPath(pathname: string | null | undefined): LoginMode {
  const path = normalize(pathname);
  if (path === ADMIN_LOGIN_PATH) return "admin";
  if (path === FOSTER_LOGIN_PATH) return "foster";
  const segment = path.replace(/^\//, "");
  return ADMIN_SEGMENTS.has(segment) ? "admin" : "foster";
}

/** The canonical login route for a mode. */
export function loginPathForMode(mode: LoginMode): string {
  return mode === "admin" ? ADMIN_LOGIN_PATH : FOSTER_LOGIN_PATH;
}

/** The canonical login route an unauthenticated visit to `pathname` is redirected to. */
export function loginPathForPath(pathname: string | null | undefined): string {
  return loginPathForMode(loginModeForPath(pathname));
}

/**
 * The login route to drop a principal back on at logout. Foster families return to the magic-code
 * page; every other (password) role returns to /aerie.
 */
export function loginPathForRole(role: Role): string {
  return role === "foster" ? FOSTER_LOGIN_PATH : ADMIN_LOGIN_PATH;
}
